import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { customersApi } from '../../services/api';

export interface CustomerActivityTabProps {
  uuid: string;
}

const CustomerActivityTab: React.FC<CustomerActivityTabProps> = ({ uuid }) => {
  const { data: history, isLoading } = useQuery({
    queryKey: ['customer-history', uuid],
    queryFn: async () => {
      const response = await customersApi.getHistory(uuid);
      return response.data.data;
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!history || history.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-6">Historique</h2>
        <div className="text-center text-gray-500 py-8">
          {`Aucun historique enregistr\u00e9 pour ce client`}
        </div>
      </div>
    );
  }

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'state_changed': return '\uD83D\uDD04';
      case 'substatus_changed': return '\uD83D\uDCDD';
      case 'info_changed': return '\u270F\uFE0F';
      case 'document_uploaded': return '\uD83D\uDCE4';
      case 'document_deleted': return '\uD83D\uDDD1\uFE0F';
      case 'onboarding_changed': return '\uD83C\uDFAF';
      case 'created': return '\u2728';
      case 'deleted': return '\u274C';
      default: return '\uD83D\uDCCB';
    }
  };

  const getActivityTitle = (type: string) => {
    switch (type) {
      case 'state_changed': return "Changement d'\u00e9tat";
      case 'substatus_changed': return 'Changement de sous-statut';
      case 'info_changed': return 'Modification des informations';
      case 'document_uploaded': return "Document upload\u00e9";
      case 'document_deleted': return "Document supprim\u00e9";
      case 'onboarding_changed': return "Changement d'onboarding";
      case 'created': return "Client cr\u00e9\u00e9";
      case 'deleted': return "Client supprim\u00e9";
      default: return "Activit\u00e9";
    }
  };

  const getFieldLabel = (fieldName: string) => {
    const labels: Record<string, string> = {
      // Documents
      'photo': "Pi\u00e8ce d'identit\u00e9 recto",
      'photo2': "Pi\u00e8ce d'identit\u00e9 verso",
      'procuration': 'Procuration',
      'libre_passage': 'Lettre de libre passage',
      'demande_suppletive': "Demande suppl\u00e9tive",
      'reponse_suppletive': "R\u00e9ponse suppl\u00e9tive",
      'reponse': "R\u00e9ponse centrale",
      'contrat_lemania': 'Contrat regroupement',
      // Customer info
      'nom': 'Nom',
      'prenom': "Pr\u00e9nom",
      'full_name': 'Nom complet',
      'sexe': 'Sexe',
      'avs': "Num\u00e9ro AVS",
      'tel': "T\u00e9l\u00e9phone",
      'dat': 'Date de naissance',
      'adr': 'Adresse',
      'npa': 'NPA',
      'loc': "Localit\u00e9",
      'pays': 'Pays',
      'civil': "\u00c9tat civil",
      'date_civil': "Date \u00e9tat civil",
      'date_separation': "Date de s\u00e9paration",
      'profession': 'Profession',
      'sit_pro': 'Situation professionnelle',
      'fondation': 'Fondation',
      'souhait': 'Souhait',
      'onboarding': 'Onboarding',
      'state': "\u00c9tat",
      'subStatus': 'Sous-statut',
      'login': 'Email',
      'sign': 'Signature',
    };
    return labels[fieldName] || fieldName;
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-lg font-medium text-gray-900 mb-6">Historique</h2>
      <div className="space-y-4">
        {history.map((entry: typeof history[number]) => (
          <div
            key={entry.id}
            className="border-2 rounded-lg p-4 bg-purple-50 border-purple-200 transition-all"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl">{getActivityIcon(entry.activity_type)}</span>
                  <h3 className="font-medium text-gray-900">{getActivityTitle(entry.activity_type)}</h3>
                </div>

                <div className="mt-2 text-sm text-gray-600 space-y-1">
                  {entry.field_name && (
                    <div className="text-gray-700">
                      <span className="font-medium">Champ:</span> {getFieldLabel(entry.field_name)}
                    </div>
                  )}

                  {entry.old_value && entry.new_value && (
                    <div>
                      <span className="text-red-600">{entry.old_value}</span>
                      {' \u2192 '}
                      <span className="text-green-600">{entry.new_value}</span>
                    </div>
                  )}

                  {!!(entry.metadata && entry.metadata.changes) && (
                    <div className="mt-2 pt-2 border-t border-purple-200">
                      <span className="font-medium">Changements:</span>
                      <ul className="list-disc list-inside mt-1 space-y-1">
                        {Object.entries(entry.metadata.changes as Record<string, { old?: string; new?: string }>).map(([field, change]) => (
                          <li key={field}>
                            <span className="font-medium">{getFieldLabel(field)}:</span>{' '}
                            <span className="text-red-600">{change.old || '(vide)'}</span>
                            {' \u2192 '}
                            <span className="text-green-600">{change.new || '(vide)'}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {!!(entry.metadata && entry.metadata.comment) && (
                    <div className="mt-2 pt-2 border-t border-purple-200">
                      <span className="font-medium">Commentaire:</span>{' '}
                      <span className="text-gray-700">{String(entry.metadata.comment)}</span>
                    </div>
                  )}
                </div>

                <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
                  <span>
                    {'\uD83D\uDCC5 '}{new Date(entry.created_at).toLocaleString('fr-FR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                  {entry.user_name && (
                    <span>{'\uD83D\uDC64 '}{entry.user_name}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CustomerActivityTab;
